/**
 * KDS — Kitchen Display System em quadro Kanban.
 *
 * Colunas por status; arrastar entre colunas avança o status (transições
 * válidas), arrastar dentro da coluna reordena manualmente (persistido —
 * prevalece sobre a ordem de chegada). Os botões continuam funcionando.
 * Itens podem ser concluídos apenas com o pedido em preparo.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { ordersApi, kitchensApi, config } from '../lib/api.js'
import type { OrderDetailed } from '../lib/api.js'
import { getTokenPayload, hasAnyRole } from '../lib/auth.js'
import { useRealtime } from '../lib/realtime.js'
import { OrdersKanban } from '../components/OrdersKanban.js'
import type { KanbanColumn, KanbanStatus } from '../components/OrdersKanban.js'
import { Button, Empty, Spinner, toast } from '../components/ui/index.js'
import { cn } from '../lib/utils.js'
import { useI18n } from '../lib/i18n.js'

const STATUS_KEY: Record<OrderDetailed['status'], string> = {
  pending: 'admin.status.pending',
  confirmed: 'admin.status.confirmed',
  preparing: 'admin.status.preparing',
  ready: 'admin.status.ready',
  delivered: 'admin.status.delivered',
  cancelled: 'admin.status.cancelled',
  // fluxo garçom (0085) — nunca chegam ao KDS (visão por mesa do garçom)
  waiter_sent: 'admin.waiter.flow_sent',
  waiter_ack: 'admin.waiter.flow_ack',
  relayed: 'admin.waiter.flow_relayed',
}

/** Transições válidas do fluxo (espelho do service) — alvo por status. */
const NEXT_STATUS: Record<KanbanStatus, KanbanStatus[]> = {
  pending: ['confirmed', 'cancelled'],
  confirmed: ['preparing', 'cancelled'],
  preparing: ['ready'],
  ready: [],
  delivered: [],
  cancelled: [],
  waiter_sent: ['waiter_ack', 'cancelled'],
  waiter_ack: ['relayed', 'cancelled'],
  relayed: [],
}
/** Cozinha só avança o preparo; entregar/cancelar é com o garçom/gestor. */
const KITCHEN_TARGETS: KanbanStatus[] = ['confirmed', 'preparing', 'ready']

/** SLA visual (#133): limiar padrão 15 min — o valor do dono vence. */
const DEFAULT_SLA_SEC = 15 * 60

function elapsedLabel(createdAt: number): string {
  const sec = Math.max(0, Math.floor(Date.now() / 1000) - createdAt)
  const min = Math.floor(sec / 60)
  return min >= 60 ? `${Math.floor(min / 60)}h${min % 60}` : `${min} min`
}

export function Kitchen() {
  const queryClient = useQueryClient()
  const { t, lang } = useI18n()
  const [filter, setFilter] = useState<'active' | 'all'>('active')
  /** estação do monitor: 'all' | 'null' (principal) | uuid */
  const [station, setStation] = useState('all')
  const [, forceTick] = useState(0)

  const { data: kitchenData } = useQuery({
    queryKey: ['kitchens'],
    queryFn: () => kitchensApi.list(),
  })
  const stations = kitchenData?.kitchens ?? []

  // #133: SLA configurável — gestor lê do /admin/config; cozinha, do menu
  // público (role kitchen não acessa /admin/config). Ausente = 15 min.
  const canReadConfig = hasAnyRole(getTokenPayload(), [
    'manager',
    'restaurant_owner',
    'platform_owner',
  ])
  const { data: cfg } = useQuery({
    queryKey: ['config'],
    queryFn: config.get,
    enabled: canReadConfig,
  })
  const { data: publicSla } = useQuery({
    queryKey: ['restaurant-sla'],
    queryFn: config.slaMinutes,
    enabled: !canReadConfig,
    staleTime: 10 * 60_000,
  })
  const slaSec = (cfg?.sla_minutes ?? publicSla ?? 15) * 60 || DEFAULT_SLA_SEC

  const { data: orders, isLoading } = useQuery({
    queryKey: ['orders', 'kitchen', filter, station, lang],
    queryFn: () =>
      ordersApi.list({
        ...(filter === 'active' ? { status: 'pending,confirmed,preparing,ready' } : {}),
        locale: lang,
        ...(station !== 'all' ? { kitchen_id: station } : {}),
      }),
    refetchInterval: 30_000, // fallback se o WS cair
  })

  const onEvent = useCallback(
    (event: { type: string }) => {
      if (event.type.startsWith('order.')) {
        void queryClient.invalidateQueries({ queryKey: ['orders'] })
        if (event.type === 'order.created') {
          toast.success(t('admin.kitchen.new_order_toast'))
          forceTick((n) => n + 1)
        }
      }
    },
    [queryClient],
  )
  const wsStatus = useRealtime({ role: 'kitchen', onEvent })

  // Reconexão (#153): eventos emitidos com o socket fora do ar se perdem;
  // ao voltar a "open" a fila é refeita na hora, sem esperar o polling.
  const wasOpen = useRef(false)
  useEffect(() => {
    if (wsStatus === 'open') {
      if (wasOpen.current) void queryClient.invalidateQueries({ queryKey: ['orders'] })
      wasOpen.current = true
    }
  }, [wsStatus, queryClient])

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: OrderDetailed['status'] }) =>
      ordersApi.updateStatus(id, status),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['orders'] }),
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : t('admin.kitchen.status_err')),
  })

  const itemMutation = useMutation({
    mutationFn: ({
      orderId,
      itemId,
      isDone,
    }: {
      orderId: string
      itemId: string
      isDone: boolean
    }) => ordersApi.setItemDone(orderId, itemId, isDone),
    onSuccess: (result) => {
      if (result.all_done) toast.success(t('admin.kitchen.all_done_toast'))
      void queryClient.invalidateQueries({ queryKey: ['orders'] })
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : t('admin.kitchen.item_err')),
  })

  const reorderMutation = useMutation({
    mutationFn: (orderIds: string[]) => ordersApi.reorder(orderIds),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['orders'] }),
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : t('admin.orders.reorder_err')),
  })

  const statusLabel = (st: KanbanStatus) => t(STATUS_KEY[st])
  const columns: KanbanColumn[] = (
    filter === 'active'
      ? (['pending', 'confirmed', 'preparing', 'ready'] as const)
      : (['pending', 'confirmed', 'preparing', 'ready', 'delivered', 'cancelled'] as const)
  ).map((st) => ({ id: st, status: st, label: t(`admin.kitchen.col.${st}`) }))

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.nav.kitchen')}</h1>
          <p className="text-sm text-gray-500">
            {t('admin.kitchen.hint')} ·{' '}
            <span
              data-testid="kitchen-realtime"
              data-status={wsStatus}
              className={cn(
                'font-medium',
                wsStatus === 'open' ? 'text-green-600' : 'text-amber-600',
              )}
            >
              {wsStatus === 'open' ? t('admin.waiter.rt_on') : t('admin.waiter.rt_off')}
            </span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {stations.length > 0 && (
            <div
              className="flex flex-wrap gap-1 rounded-lg border border-indigo-200 bg-indigo-50/50 p-1"
              data-testid="kitchen-stations"
            >
              <button
                data-testid="kitchen-station-all"
                data-active={station === 'all'}
                onClick={() => setStation('all')}
                className={cn(
                  'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                  station === 'all'
                    ? 'bg-indigo-600 text-white'
                    : 'text-indigo-700 hover:bg-indigo-100',
                )}
              >
                {t('admin.kitchen.all_stations')}
              </button>
              <button
                data-testid="kitchen-station-main"
                data-active={station === 'null'}
                onClick={() => setStation('null')}
                className={cn(
                  'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                  station === 'null'
                    ? 'bg-indigo-600 text-white'
                    : 'text-indigo-700 hover:bg-indigo-100',
                )}
              >
                {t('admin.kitchens.main')}
              </button>
              {stations.map((k) => (
                <button
                  key={k.id}
                  data-testid={`kitchen-station-${k.id}`}
                  data-active={station === k.id}
                  onClick={() => setStation(k.id)}
                  className={cn(
                    'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                    station === k.id
                      ? 'bg-indigo-600 text-white'
                      : 'text-indigo-700 hover:bg-indigo-100',
                    !k.active && 'opacity-50',
                  )}
                >
                  {k.name}
                </button>
              ))}
            </div>
          )}
          <div className="flex gap-1 rounded-lg border border-gray-200 bg-white p-1">
            {(['active', 'all'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={cn(
                  'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                  filter === f ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-100',
                )}
              >
                {f === 'active' ? t('admin.kitchen.filter_active') : t('admin.kitchen.filter_all')}
              </button>
            ))}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="w-6 h-6 text-indigo-500" />
        </div>
      ) : (orders ?? []).length === 0 ? (
        <Empty title={t('admin.kitchen.empty')} description={t('admin.kitchen.empty_hint')} />
      ) : (
        <OrdersKanban
          orders={orders ?? []}
          columns={columns}
          canMove={(order, target) => {
            if (!NEXT_STATUS[order.status].includes(target)) {
              toast.error(
                t('admin.orders.invalid_move', {
                  from: statusLabel(order.status),
                  to: statusLabel(target),
                }),
              )
              return false
            }
            if (!KITCHEN_TARGETS.includes(target)) {
              toast.error(t('admin.kitchen.waiter_only'))
              return false
            }
            // MULTIESTAÇÃO: uma cozinha não antecipa as outras — o pedido
            // só fica pronto quando TODAS as estações concluem seus itens
            if (target === 'ready' && order.multi_station && !order.all_items_done) {
              toast.error(t('admin.kitchen.ready_blocked'))
              return false
            }
            return true
          }}
          onMove={(order, target) => statusMutation.mutate({ id: order.id, status: target })}
          onReorder={(_status, ids) => reorderMutation.mutate(ids)}
          groupKey={(o) =>
            o.channel === 'dine_in'
              ? `${t('admin.orders.table')} ${o.table_label ?? o.table_number ?? '?'}`
              : o.channel === 'takeaway'
                ? t('admin.channel.takeaway')
                : t('admin.channel.delivery')
          }
          subgroupKey={(o) => o.reference_name}
          renderCard={(order) => (
            <OrderCard
              order={order}
              slaSec={slaSec}
              onStatus={(status) => statusMutation.mutate({ id: order.id, status })}
              onItem={(itemId, isDone) =>
                itemMutation.mutate({ orderId: order.id, itemId, isDone })
              }
            />
          )}
        />
      )}
    </div>
  )
}

function OrderCard({
  order,
  slaSec,
  onStatus,
  onItem,
}: {
  order: OrderDetailed
  /** limiar do destaque em segundos (#133) */
  slaSec: number
  onStatus: (status: OrderDetailed['status']) => void
  onItem: (itemId: string, isDone: boolean) => void
}) {
  const { t } = useI18n()
  const waiting = Date.now() / 1000 - order.created_at > slaSec
  const allDone = order.items.length > 0 && order.items.every((i) => i.is_done)
  // Concluir item só faz sentido durante o preparo
  const itemActionsEnabled = order.status === 'preparing'
  // ESTAÇÃO concluiu os SEUS itens mas o pedido (multiestação) ainda tem
  // preparo em outras cozinhas
  const waitingOthers = Boolean(order.multi_station) && allDone && !order.all_items_done

  return (
    <div
      data-testid={`order-${order.id}`}
      className={cn(
        'rounded-xl border bg-white shadow-sm flex flex-col cursor-grab active:cursor-grabbing overflow-hidden',
        order.status === 'pending'
          ? waiting
            ? 'border-red-300 ring-1 ring-red-200'
            : 'border-amber-300'
          : 'border-gray-200',
      )}
    >
      {/* Cronômetro — mesa e status já estão na coluna e no grupo */}
      <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-gray-100">
        {waitingOthers ? (
          <span className="text-[11px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded whitespace-nowrap">
            ⏳ {t('admin.kitchen.waiting_stations')}
          </span>
        ) : (
          <span />
        )}
        <span
          className={cn(
            'text-xs font-bold tabular-nums whitespace-nowrap px-1.5 py-0.5 rounded',
            waiting ? 'text-red-700 bg-red-100' : 'text-gray-500 bg-gray-100',
          )}
        >
          ⏱ {elapsedLabel(order.created_at)}
        </span>
      </div>

      {order.customer_name && (
        <p className="px-3 pt-1.5 text-xs text-gray-500 truncate">
          {t('admin.orders.customer')}: {order.customer_name}
        </p>
      )}

      <ul className="px-3 py-1.5 space-y-1.5 flex-1 min-w-0">
        {order.items.map((item) => (
          <li
            key={item.id}
            data-testid={`order-item-${item.id}`}
            data-done={item.is_done}
            className={cn(
              'rounded-lg border px-2.5 py-1.5 transition-colors min-w-0',
              item.is_done ? 'border-green-200 bg-green-50' : 'border-gray-200',
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p
                  className={cn(
                    'text-sm font-medium truncate',
                    item.is_done ? 'text-green-800 line-through' : 'text-gray-900',
                  )}
                >
                  {item.quantity}× {item.product_name}
                  {item.variant_label && (
                    <span className="font-semibold text-gray-500"> ({item.variant_label})</span>
                  )}
                </p>
                {item.removed_ingredients.length > 0 && (
                  <p className="text-xs text-red-600 mt-0.5">
                    {t('admin.orders.without')}: {item.removed_ingredients.join(', ')}
                  </p>
                )}
                {item.extras.length > 0 && (
                  <p className="text-xs text-indigo-600 mt-0.5">
                    + {item.extras.map((e) => `${e.quantity}× ${e.name}`).join(', ')}
                  </p>
                )}
                {item.notes && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t('admin.orders.notes')}: {item.notes}
                  </p>
                )}
              </div>
              {itemActionsEnabled ? (
                <button
                  data-testid={`order-item-done-${item.id}`}
                  onClick={() => onItem(item.id, !item.is_done)}
                  className={cn(
                    'shrink-0 rounded-md px-2 py-1 text-xs font-semibold transition-colors',
                    item.is_done
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                  )}
                >
                  {item.is_done ? `✓ ${t('admin.orders.done')}` : t('admin.kitchen.finish_item')}
                </button>
              ) : (
                item.is_done && (
                  <span className="shrink-0 text-xs font-semibold text-green-700">
                    ✓ {t('admin.orders.done')}
                  </span>
                )
              )}
            </div>
          </li>
        ))}
      </ul>

      {order.notes && (
        <p className="mx-3 mb-1.5 rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-xs text-amber-800">
          {order.notes}
        </p>
      )}

      <div className="px-3 py-2 border-t border-gray-100 flex gap-2">
        {order.status === 'pending' && (
          <Button size="sm" className="flex-1" onClick={() => onStatus('confirmed')}>
            {t('admin.orders.confirm')}
          </Button>
        )}
        {order.status === 'confirmed' && (
          <Button size="sm" className="flex-1" onClick={() => onStatus('preparing')}>
            {t('admin.kitchen.start_preparing')}
          </Button>
        )}
        {order.status === 'preparing' && (
          <Button
            size="sm"
            className="flex-1 bg-green-600 hover:bg-green-700"
            onClick={() => onStatus('ready')}
          >
            {allDone ? t('admin.status.ready') : t('admin.kitchen.mark_ready')}
          </Button>
        )}
        {order.status === 'ready' && (
          <p className="text-xs text-green-700 font-medium py-2">
            {t('admin.kitchen.ready_waiting')}
          </p>
        )}
      </div>
    </div>
  )
}
